/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit';

import { PLACE_RESULT_TYPE, PROVIDER_RESULT_TYPE } from 'utils/constants';
import { SPECIALTIES_SLICE_NAME } from '../slicesNames';

export const specialties = (state) => state[SPECIALTIES_SLICE_NAME].specialties;

export const subspecialties = (state) => state[SPECIALTIES_SLICE_NAME].subspecialties;

export const specialtiesNameMap = (state) => state[SPECIALTIES_SLICE_NAME].specialtiesNameMap;

export const subspecialtiesNameMap = (state) => state[SPECIALTIES_SLICE_NAME].subspecialtiesNameMap;

export const placeSpecialtiesMap = (state) =>
  state[SPECIALTIES_SLICE_NAME].specialties?.[PLACE_RESULT_TYPE];

export const placeSubspecialtiesMap = (state) =>
  state[SPECIALTIES_SLICE_NAME].subspecialties?.[PLACE_RESULT_TYPE];

export const placeSpecialtiesArray = createSelector([placeSpecialtiesMap], (specialtyMap) =>
  specialtyMap ? Object.values(specialtyMap) : null
);

/* ************************** */
/*  Facility Classifications  */
/* ************************** */

/** Returns the facility classification types hashed by embold_id
 * @returns {object}
 * @example
 * {
 *   "123": {
 *     "emboldId": "123",
 *     "name": "Hospital",
 *     "description": ""
 *   }
 * }
 */
export const facilityTypesMap = (state) => state[SPECIALTIES_SLICE_NAME].facilityTypes;

/** Returns the facility classification types hashed by name to lower case
 * @returns {object}
 * @example
 * {
 *   "hospital": {
 *     "emboldId": "123",
 *     "name": "Hospital",
 *     "description": ""
 *   }
 * }
 */
export const facilityTypesNameMap = (state) => state[SPECIALTIES_SLICE_NAME].facilityTypesNameMap;

/** Returns the facility classification focus areas hashed by embold_id
 * @returns {object}
 * @example
 * {
 *   "123": {
 *     "emboldId": "123",
 *     "name": "General Acute Care",
 *     "description": ""
 *   }
 * }
 */
export const facilityFocusAreasMap = (state) => state[SPECIALTIES_SLICE_NAME].facilityFocusAreas;

/** Returns an array of facility classification types
 * @param {object} state - The Redux state
 * @returns {Array<object>} Array of facility type objects
 * @example
 * [
 *   {
 *     "emboldId": "123",
 *     "name": "Hospital",
 *     "description": ""
 *   },
 *   {
 *     "emboldId": "456",
 *     "name": "Clinic",
 *     "description": ""
 *   }
 * ]
 */
export const facilityTypesArray = createSelector([facilityTypesMap], (facilityTypesObj) =>
  Object.values(facilityTypesObj)
);

/** Returns the facility classification focus areas hashed by name to lower case
 * @returns {object}
 * @example
 * {
 *   "general acute care": {
 *     "emboldId": "123",
 *     "name": "General Acute Care",
 *     "description": ""
 *   }
 * }
 */
export const facilityFocusAreasNameMap = (state) =>
  state[SPECIALTIES_SLICE_NAME].facilityFocusAreasNameMap;

export const providerSpecialtiesMap = (state) =>
  state[SPECIALTIES_SLICE_NAME].specialties?.[PROVIDER_RESULT_TYPE];

export const providerSpecialtiesArray = createSelector([providerSpecialtiesMap], (specialtyMap) =>
  specialtyMap ? Object.values(specialtyMap) : null
);

export const providerSubspecialtiesMap = (state) =>
  state[SPECIALTIES_SLICE_NAME].subspecialties?.[PROVIDER_RESULT_TYPE];

export const providerSubspecialtiesArray = createSelector(
  [providerSubspecialtiesMap],
  (subspecialtyMap) => (subspecialtyMap ? Object.values(subspecialtyMap) : null)
);
export const loadingSpecialties = (state) => state[SPECIALTIES_SLICE_NAME].loadingSpecialties;

export const failedToLoadSpecialties = (state) =>
  state[SPECIALTIES_SLICE_NAME].failedToLoadSpecialties;

export function bySpecialtyId(id) {
  return createSelector(
    [providerSpecialtiesMap, placeSpecialtiesMap],
    (providerSpecialtiesMap, placeSpecialtiesMap) => {
      if (id === undefined) return undefined;
      return placeSpecialtiesMap[id] || providerSpecialtiesMap[id];
    }
  );
}

export function bySubspecialtyId(id) {
  return createSelector(
    [providerSubspecialtiesMap, placeSubspecialtiesMap],
    (providerSubspecialties, placeSubspecialties) => {
      if (id === undefined) return undefined;
      return placeSubspecialties[id] || providerSubspecialties[id];
    }
  );
}
